import { useState } from "react";
import { ApiError } from "@/api/client";
import {
  sourceFileUrl,
  useReplaceSourceFile,
  type ConnectionDetail,
  type FileReplaced,
  type SourceFile,
} from "@/api/queries";
import { FileDrop } from "@/components/sources/FileDrop";
import { Button } from "@/components/ui/Button";
import { Chip } from "@/components/ui/Chip";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { DownloadIcon, SwapIcon } from "@/components/icons";
import { formatBytes } from "@/lib/bytes";

function stamp(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleString() : "—";
}

function isDatabase(file: SourceFile): boolean {
  return file.kind === "sqlite" || file.kind === "duckdb";
}

function family(file: SourceFile): string {
  return isDatabase(file) ? `another ${file.kind_label}` : "a CSV/TSV, Excel, Parquet or JSON file";
}

/**
 * The file an uploaded source is (FILE-3). *Replace* swaps it under the same
 * source — its notes, relationships, conversations and tiles stay — and the
 * confirmation names the tables a reader stands to lose, because which ones
 * actually go is only known once the new file has been read; the result says
 * which did.
 */
export function SourceFileCard({ connection }: { connection: ConnectionDetail }) {
  const file = connection.file;
  const replace = useReplaceSourceFile(connection.id);
  const [next, setNext] = useState<File | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<FileReplaced | null>(null);

  if (!file) return null;

  const run = () => {
    if (!next) return;
    setConfirming(false);
    setError(null);
    setResult(null);
    setProgress(0);
    replace.mutate(
      { file: next, onProgress: setProgress },
      {
        onSuccess: (replaced) => {
          setResult(replaced);
          setNext(null);
          setProgress(null);
        },
        onError: (caught) => {
          setProgress(null);
          setError(caught instanceof ApiError ? caught.message : "The file could not be replaced.");
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-6">
      <section className="rounded-container border border-border bg-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="type-h4 text-ink flex flex-wrap items-center gap-2">
              <span className="truncate">{file.original_name}</span>
              <Chip tone="neutral">{file.kind_label}</Chip>
              <Chip tone="accent">version {file.version}</Chip>
            </h3>
            <dl className="type-support text-ink-secondary mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
              <dt className="text-ink-disabled">Size</dt>
              <dd className="tabular-nums">{formatBytes(file.bytes)}</dd>
              <dt className="text-ink-disabled">Uploaded</dt>
              <dd>{stamp(file.uploaded_at)}</dd>
              {file.replaced_at ? (
                <>
                  <dt className="text-ink-disabled">Replaced</dt>
                  <dd>{stamp(file.replaced_at)}</dd>
                </>
              ) : null}
              <dt className="text-ink-disabled">SHA-256</dt>
              <dd className="type-code truncate" title={file.sha256}>
                {file.sha256.slice(0, 16)}…
              </dd>
            </dl>
          </div>
          <a
            href={sourceFileUrl(connection.id)}
            download={file.original_name}
            className="type-label text-ink inline-flex items-center gap-2 rounded-element border border-border bg-card px-3 py-2 hover:bg-hover motion-fast"
          >
            <DownloadIcon className="size-4" />
            Download
          </a>
        </div>

        <div className="mt-4">
          <p className="type-label text-ink mb-2">
            {file.tables.length} table{file.tables.length === 1 ? "" : "s"}
          </p>
          <ul className="flex flex-wrap gap-2">
            {file.tables.map((table) => (
              <li key={table.name}>
                <Chip tone="neutral">
                  {table.name}
                  {table.sheet && table.sheet.toLowerCase() !== table.name ? ` ← ${table.sheet}` : ""}
                  {typeof table.rows === "number" ? ` · ${table.rows.toLocaleString()} rows` : ""}
                </Chip>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="rounded-container border border-border bg-card p-5">
        <h3 className="type-h4 text-ink">Replace the file</h3>
        <p className="type-support text-ink-secondary mt-1 mb-4">
          Upload {family(file)}. The source keeps its notes, relationships, conversations and
          tiles; the new file is checked before it takes over, and a query running meanwhile reads
          one file or the other.
        </p>
        <FileDrop
          file={next}
          progress={progress}
          disabled={replace.isPending}
          label="Drop the new version here, or choose it"
          hint={isDatabase(file) ? `A ${file.kind_label}.` : "CSV/TSV, Excel (.xlsx), Parquet or JSON."}
          onFile={(chosen) => {
            setNext(chosen);
            setError(null);
            setResult(null);
          }}
        >
          {error ? (
            <p role="alert" className="type-support text-error rounded-element bg-error-subtle p-3">
              {error}
            </p>
          ) : null}
          {result ? (
            <p role="status" className="type-support text-ink rounded-element bg-success-subtle p-3">
              Now version {result.connection.file?.version}.
              {result.added_tables.length ? ` Added: ${result.added_tables.join(", ")}.` : ""}
              {result.dropped_tables.length
                ? ` No longer present: ${result.dropped_tables.join(", ")} — tiles reading them will say so on their next refresh.`
                : ""}
            </p>
          ) : null}
          <div>
            <Button
              variant="primary"
              icon={<SwapIcon className="size-4" />}
              disabled={!next}
              loading={replace.isPending}
              onClick={() => setConfirming(true)}
            >
              Replace…
            </Button>
          </div>
        </FileDrop>
        <ConfirmDialog
          open={confirming}
          onClose={() => setConfirming(false)}
          title={`Replace ${file.original_name}?`}
          description={
            next
              ? `${connection.name} will read ${next.name} (${formatBytes(next.size)}) instead.`
              : ""
          }
          consequence={
            <>
              Of {file.tables.map((table) => table.name).join(", ")}, any table{" "}
              {next?.name ?? "the new file"} does not have is removed, and tiles reading it fail
              their next refresh. The previous version is not kept — download it first if you may
              want it back.
            </>
          }
          confirmLabel="Replace"
          onConfirm={run}
        />
      </section>
    </div>
  );
}
